import React, { createContext, useContext, useState, ReactNode } from 'react';

interface ZoomContextType {
  zoomLevel: number;
  minZoom: number;
  maxZoom: number;
  zoomStep: number;
  handleZoomIn: () => void;
  handleZoomOut: () => void;
  handleZoomReset: () => void;
}

const ZoomContext = createContext<ZoomContextType | undefined>(undefined);

interface ZoomProviderProps {
  children: ReactNode;
}

export const ZoomProvider: React.FC<ZoomProviderProps> = ({ children }) => {
  const [zoomLevel, setZoomLevel] = useState(0.8);
  const minZoom = 0.24; // 0.24 * 125 = 30%
  const maxZoom = 1.6; // 1.6 * 125 = 200%
  const zoomStep = 0.08; // 0.08 * 125 = 10%

  const handleZoomIn = () => {
    setZoomLevel((prev) => Math.min(prev + zoomStep, maxZoom));
  };

  const handleZoomOut = () => {
    setZoomLevel((prev) => Math.max(prev - zoomStep, minZoom));
  };

  const handleZoomReset = () => {
    setZoomLevel(0.8);
  };

  const value = {
    zoomLevel,
    minZoom,
    maxZoom,
    zoomStep,
    handleZoomIn,
    handleZoomOut,
    handleZoomReset,
  };

  return <ZoomContext.Provider value={value}>{children}</ZoomContext.Provider>;
};

export const useZoom = (): ZoomContextType => {
  const context = useContext(ZoomContext);
  if (context === undefined) {
    throw new Error('useZoom must be used within a ZoomProvider');
  }
  return context;
};
